/**
 * How the Search tab's numbers and runs read to a person.
 *
 * The distinction that earns its keep is WARN: a pull that ran but looks wrong
 * (the wrong property, a changed domain). It gets amber and its own words,
 * because a WARN run also means the 28/56-day rule was NOT applied that night —
 * the one thing somebody checking "why hasn't this page moved" needs to know.
 *
 * Pure and dependency-free so jest can reach it.
 */
import type { PageSearchState, SearchSyncRun } from '../../types';

export interface RunBadge {
  label: string;
  className: string;
}

const BADGES: Record<SearchSyncRun['status'], RunBadge> = {
  RUNNING: {
    label: 'Pulling…',
    className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 animate-pulse',
  },
  OK: {
    label: 'OK',
    className: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  },
  WARN: {
    label: 'Warning',
    className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-300',
  },
  ERROR: {
    label: 'Failed',
    className: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  },
  SKIP: {
    label: 'Skipped',
    className: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  },
};

export function runBadge(status: SearchSyncRun['status']): RunBadge {
  return BADGES[status] ?? BADGES.SKIP;
}

const TRANSITION_WORDS: Record<string, string> = {
  RETARGET_DUE: 'to retarget',
  RETIRE_DUE: 'up for retirement',
  LIVE: 'back to live',
};

/** One line under a run: what it covered and what it changed. */
export function runLine(run: SearchSyncRun): string {
  if (run.status === 'SKIP' || run.status === 'ERROR') return run.detail ?? '';
  const parts: string[] = [];
  if (run.date_from && run.date_to) parts.push(`${run.date_from} → ${run.date_to}`);
  parts.push(`${run.pages_matched} page${run.pages_matched === 1 ? '' : 's'} with data`);
  const moved = Object.entries(run.transitions ?? {})
    .filter(([, n]) => n > 0)
    .map(([state, n]) => `${n} ${TRANSITION_WORDS[state] ?? state.toLowerCase()}`);
  if (moved.length) parts.push(moved.join(', '));
  if (run.status === 'WARN') parts.push('pages were not aged this run');
  return parts.join(' · ');
}

/** "12%", or an em dash when there is nothing live to divide by. */
export function percent(share: number | null): string {
  if (share === null || Number.isNaN(share)) return '-';
  return `${Math.round(share * 100)}%`;
}

/** Human words for the OAuth callback's `gsc_error`. */
export function connectError(code: string): string {
  switch (code) {
    case 'access_denied':
      return 'Google access was not granted.';
    case 'no_refresh_token':
      return 'Google did not return a lasting token. Remove this app under your Google account’s third-party access, then connect again.';
    case 'invalid_state':
      return 'The connection link expired. Try connecting again.';
    case 'site_not_found':
      return 'That site no longer exists.';
    default:
      return 'Connecting to Search Console failed. Try again.';
  }
}

/** The small tag on a page row, or null for a page search has no opinion on.
 *  LIVE and KEPT say nothing: a badge on every healthy page would be noise. */
export function searchStateBadge(state: PageSearchState | null | undefined): RunBadge | null {
  switch (state) {
    case 'RETARGET_DUE':
      return {
        label: 'Retarget',
        className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-300',
      };
    case 'RETIRE_DUE':
      return {
        label: 'Retire?',
        className: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
      };
    case 'RETIRED':
      return {
        label: 'Retired',
        className: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      };
    default:
      return null;
  }
}
